/**
 * Features a plan can include. The platform admin ticks them per plan; the API refuses what a
 * customer's plan does not include, and the dashboard shows those parts locked with a way to
 * upgrade. Limits (screens, storage, users) are separate: they are numbers on the plan.
 *
 * Anything not listed here is available on every plan.
 */
export const PLAN_FEATURES = [
  {
    key: 'custom_designs',
    label: 'Custom menu designs',
    labelAr: 'التصاميم الخاصة',
    description: 'Design your own menu boards in the editor. Ready-made designs are on every plan.',
  },
  {
    key: 'scheduling',
    label: 'Scheduling',
    labelAr: 'الجدولة',
    description: 'Time slots, days of the week, dates and priorities. Always-on content is on every plan.',
  },
  {
    key: 'email_alerts',
    label: 'Email alerts',
    labelAr: 'تنبيهات بالبريد الإلكتروني',
    description: 'Emails when a screen goes offline, stops syncing or runs low on space.',
  },
  {
    key: 'floor_plans',
    label: 'Floor plans',
    labelAr: 'المخططات',
    description: 'Place screens on a picture of each location and see them live.',
  },
  {
    key: 'remote_screenshots',
    label: 'Remote screenshots',
    labelAr: 'لقطات شاشة عن بُعد',
    description: 'See what a screen is showing right now, from the dashboard.',
  },
  {
    key: 'proof_of_play',
    label: 'Proof of play and analytics',
    labelAr: 'إثبات العرض والتحليلات',
    description: 'Play analytics and downloadable proof-of-play reports.',
  },
  {
    key: 'webhooks',
    label: 'Webhooks',
    labelAr: 'الربط عبر Webhooks',
    description: 'Send alerts to your own systems.',
  },
  {
    key: 'api_access',
    label: 'API access',
    labelAr: 'الواجهة البرمجية API',
    description: 'API keys for connecting other software.',
  },
] as const;

export type PlanFeature = (typeof PLAN_FEATURES)[number]['key'];
export const PLAN_FEATURE_KEYS = PLAN_FEATURES.map((f) => f.key) as [PlanFeature, ...PlanFeature[]];

export function planFeatureLabel(key: PlanFeature): string {
  return PLAN_FEATURES.find((f) => f.key === key)?.label ?? key;
}

/** What each default plan includes when it is first created. */
export const DEFAULT_PLAN_FEATURES: Record<string, PlanFeature[]> = {
  // The trial shows everything, so people can try what they would be paying for.
  trial: [...PLAN_FEATURE_KEYS],
  free: [],
  basic: ['custom_designs', 'scheduling', 'email_alerts'],
  business: ['custom_designs', 'scheduling', 'email_alerts', 'floor_plans', 'remote_screenshots'],
  pro: ['custom_designs', 'scheduling', 'email_alerts', 'floor_plans', 'remote_screenshots', 'proof_of_play', 'webhooks'],
};

/** One line of a plan's feature list, as the website and billing page show it. */
export interface PlanFeatureLine {
  /** Stable id: a feature key, or `screens`, `storage`, `users`, `core-*`. */
  key: string;
  label: string;
  labelAr: string;
  /** False for a feature the plan does not include: shown crossed out, to compare plans. */
  included: boolean;
}

const UNLIMITED_SCREENS = 100_000;

function sizeText(bytes: number): { en: string; ar: string } {
  const gb = bytes / 1024 ** 3;
  if (gb >= 1024) {
    const tb = Math.round((gb / 1024) * 10) / 10;
    return { en: `${tb} TB`, ar: `${tb} تيرابايت` };
  }
  const value = gb >= 10 ? Math.round(gb) : Math.round(gb * 10) / 10;
  return { en: `${value} GB`, ar: `${value} جيجابايت` };
}

/** Arabic count wording: 1 شاشة واحدة, 2 شاشتان, 3–10 شاشات, 11+ شاشة. */
function arabicCount(n: number, one: string, two: string, few: string, many: string): string {
  if (n === 1) return one;
  if (n === 2) return two;
  if (n >= 3 && n <= 10) return `${n} ${few}`;
  return `${n} ${many}`;
}

/**
 * A plan's feature list, built from what the system enforces: its limits, what every plan has,
 * and each feature, ticked or not. Nothing is typed by hand, so the list cannot promise what
 * the plan does not give.
 */
export function planFeatureLines(plan: {
  maxScreens: number;
  maxStorageBytes: number;
  maxUsers: number;
  capabilities: readonly string[];
}): PlanFeatureLine[] {
  const unlimited = plan.maxScreens >= UNLIMITED_SCREENS;
  const storage = sizeText(plan.maxStorageBytes);
  const lines: PlanFeatureLine[] = [
    {
      key: 'screens',
      label: unlimited ? 'Unlimited screens' : plan.maxScreens === 1 ? '1 screen' : `${plan.maxScreens} screens`,
      labelAr: unlimited ? 'شاشات غير محدودة' : arabicCount(plan.maxScreens, 'شاشة واحدة', 'شاشتان', 'شاشات', 'شاشة'),
      included: true,
    },
    {
      key: 'storage',
      label: `${storage.en} media storage`,
      labelAr: `مساحة ${storage.ar} للوسائط`,
      included: true,
    },
    {
      key: 'users',
      label: plan.maxUsers === 1 ? '1 team member' : `${plan.maxUsers} team members`,
      labelAr: arabicCount(plan.maxUsers, 'عضو واحد في الفريق', 'عضوان في الفريق', 'أعضاء في الفريق', 'عضوًا في الفريق'),
      included: true,
    },
    {
      key: 'core-content',
      label: 'Playlists, slideshows and ready-made menu boards',
      labelAr: 'قوائم التشغيل والعروض ولوحات المنيو الجاهزة',
      included: true,
    },
    {
      key: 'core-offline',
      label: 'Keeps playing when the internet drops',
      labelAr: 'يستمر العرض عند انقطاع الإنترنت',
      included: true,
    },
  ];
  for (const feature of PLAN_FEATURES) {
    lines.push({
      key: feature.key,
      label: feature.label,
      labelAr: feature.labelAr,
      included: plan.capabilities.includes(feature.key),
    });
  }
  return lines;
}
